// Pure search logic for the Product Facilities TE-ID field: restricts the
// searchable set to the seller SC's own facilities plus the TE-99999999
// non-certified sentinel (86ajw7fuj), and reports when a fully-typed TE-ID
// matches nothing in that set so the caller can surface an error.

export const NON_CERTIFIED_FACILITY_TE_ID = "TE-99999999";

export interface FacilityOption {
  TE_ID: string;
  account_name: string;
}

export function buildFacilitySearchOptions(sellerScFacilityTeIds: string[]): FacilityOption[] {
  const ids = Array.from(new Set([...sellerScFacilityTeIds, NON_CERTIFIED_FACILITY_TE_ID]));
  return ids.map((teId) => ({ TE_ID: teId, account_name: "" }));
}

export interface FacilitySearchResult {
  matches: FacilityOption[];
  /**
   * The typed query, reconstructed as "TE-<digits>", when it matched no
   * facility on the seller SC; null when there was a match (or no query
   * was typed yet). Set for any non-empty query, complete or partial, so
   * this mirrors what "No Results" in the dropdown already tells the user
   * -- a live TE-ID search that turned up nothing.
   */
  notFoundQuery: string | null;
}

export function searchFacilityTeIds(
  query: string,
  facilitySearchOptions: FacilityOption[]
): FacilitySearchResult {
  const trimmed = query.trim();
  const normalizedQuery = trimmed.toLowerCase();

  if (!normalizedQuery) {
    return { matches: facilitySearchOptions, notFoundQuery: null };
  }

  const matches = facilitySearchOptions.filter((option) =>
    option.TE_ID.toLowerCase().includes(normalizedQuery)
  );

  if (matches.length > 0) {
    return { matches, notFoundQuery: null };
  }

  // The search field strips the "TE-" prefix (restrictInputFormat), so the
  // query normally arrives as bare digits; still accept an already-prefixed
  // value defensively.
  const digitsOnly = trimmed.replace(/^TE-?/i, "");
  return { matches, notFoundQuery: `TE-${digitsOnly}` };
}
